/**
 * ResultTable - a report builder result as a real table (docs/plans/crm-report-builder-search-collab-plan.md
 * A4.2, A4.4, B4.6; work package R-7), drawn from builderLogic pivot and nothing else.
 *
 * One value column (a single grouping, a measure by stage with no grouping, or no split at all): each
 * row carries a CSS bar (BarRow's look and builderLogic barPct's width, components/reports/BarRow.tsx)
 * beside its number. Two ways (a grouping by a second grouping or by stage, or a grouping by time
 * shown as a table): a grid whose cells are shaded by size (the Productivity heat ramp) when bars are
 * on, plain numbers when the chart is "table". Stage and status bars and dots keep the lifecycle data
 * colors (lib lifecycleMeta).
 *
 * Nothing is hidden: the missing-value rows and columns ("(none)", "(no criterion)", "(default)") and
 * "Other (N more)" come last, as pivot orders them, styled muted (grey, italic) but always shown. A
 * cell with no row behind it is a real zero, shown faint. The header row sticks while the table
 * scrolls, the first column sticks while it scrolls sideways, and numbers are tabular. Talk time
 * comes back in seconds and is shown in minutes.
 */
import { Z } from '../components/ui';
import { lifecycleMeta } from '../lib';
import { displayValue, formatValue } from './builderLogic';
import type { PivotHeader, PivotRow, PivotTable } from './builderLogic';
import type { ReportUnit } from './types';

export interface ResultTableProps {
  table: PivotTable;
  /** Bars in the value column and shading in a grid (chart "bars"); numbers only for "table". */
  bars: boolean;
  /** What the numbers are, for screen readers ("New leads by study"). */
  caption: string;
}

/** A number for a grid cell: minutes for talk time, no unit word (the header and the line above say it). */
export function cellText(value: number, unit: ReportUnit): string {
  return displayValue(value, unit).toLocaleString('en-US', { maximumFractionDigits: 1 });
}

const muted = (h: PivotHeader): boolean => h.isOther || h.isNone;

/** The Productivity grid's primary heat ramp, by the cell's share of the largest cell; grey for muted rows. */
export function heatClass(pct: number, mutedRow: boolean): string {
  if (!(pct > 0)) return '';
  if (mutedRow) return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
  const r = pct / 100;
  if (r > 0.8) return 'bg-primary-600 text-white';
  if (r > 0.6) return 'bg-primary-500 text-white';
  if (r > 0.4) return 'bg-primary-400 text-white dark:text-gray-900';
  if (r > 0.2) return 'bg-primary-200 text-primary-900 dark:bg-primary-800 dark:text-primary-100';
  return 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200';
}

/** A bar's fill: grey for "Other" and "(none)", the lifecycle color for a stage or status, else primary. */
export function barColor(axisKind: string, h: PivotHeader): string {
  if (muted(h)) return 'bg-gray-400 dark:bg-gray-500';
  if (axisKind === 'status') return lifecycleMeta(h.key).dot;
  return 'bg-primary-500';
}

const thCls = 'px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
const headBg = 'bg-gray-50 dark:bg-gray-900';

function RowHeader({ row, sticky }: { row: PivotRow; sticky: boolean }) {
  return (
    <th
      scope="row"
      className={`px-3 py-2 text-left text-sm font-medium ${
        sticky ? `sticky left-0 ${Z.rail} bg-white dark:bg-gray-800` : ''
      } ${muted(row) ? 'italic text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}
    >
      <span className="block max-w-[14rem] truncate" title={row.long}>
        {row.label}
      </span>
    </th>
  );
}

function StatusDot({ kind, header }: { kind: string; header: PivotHeader }) {
  if (kind !== 'status' || muted(header)) return null;
  return <span aria-hidden="true" className={`mr-1.5 mt-[3px] inline-block h-2 w-2 flex-shrink-0 rounded-full ${lifecycleMeta(header.key).dot}`} />;
}

/** One value column: a row per group with its bar and its number. */
function ValueTable({ table, bars, caption }: ResultTableProps) {
  const column = table.columns[0];
  const total = table.columnTotals[0] ?? null;
  const showTotal = table.rows.length > 1 && total !== null;
  const firstHeader = table.rowAxis.label || 'Measure';
  return (
    <div className="overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 lg:max-h-[70vh]" data-rb="table">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className={`sticky top-0 ${Z.rail} ${headBg}`}>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th scope="col" className={`${thCls} text-left`}>{firstHeader}</th>
            <th scope="col" className={`${thCls} text-right`} data-rb-col={column ? column.key : undefined}>
              {column ? column.label : ''}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {table.rows.map((row) => {
            const cell = row.cells[0] ?? { value: 0, pct: 0, empty: true };
            return (
              <tr
                key={row.key}
                data-rb-row={row.key}
                data-rb-muted={muted(row) ? 'true' : undefined}
                className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                <RowHeader row={row} sticky={false} />
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-3">
                    {bars && (
                      <div className="h-2 min-w-[4rem] flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                        <div
                          data-rb-bar-fill={row.key}
                          className={`h-full rounded-full transition-all ${barColor(table.rowAxis.kind, row)}`}
                          style={{ width: `${cell.pct}%` }}
                        />
                      </div>
                    )}
                    <span
                      data-rb-value={row.key}
                      className={`w-20 flex-shrink-0 text-right tabular-nums ${
                        cell.empty ? 'text-gray-400 dark:text-gray-500' : muted(row) ? 'text-gray-600 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      {formatValue(cell.value, table.unit)}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        {showTotal && (
          <tfoot>
            <tr className="border-t border-gray-200 dark:border-gray-600" data-rb="footer">
              <th scope="row" className="px-3 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                Total
              </th>
              <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-rb-total-cell="0">
                {formatValue(total, table.unit)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/** Two ways: rows by columns, shaded by size when bars are on. */
function GridTable({ table, bars, caption }: ResultTableProps) {
  const rowTotals = table.additive;
  const showFooter = table.rows.length > 1 && table.columnTotals.some((t) => t !== null);
  const corner = [table.rowAxis.label, table.columnAxis.label].filter(Boolean).join(' / ');
  const colKind = table.columnAxis.kind;
  return (
    <div className="overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 lg:max-h-[70vh]" data-rb="table">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th scope="col" className={`sticky left-0 top-0 ${Z.menu} ${headBg} ${thCls} text-left`}>
              {corner}
            </th>
            {table.columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                title={col.long}
                data-rb-col={col.key}
                data-rb-muted={muted(col) ? 'true' : undefined}
                className={`sticky top-0 ${Z.rail} ${headBg} px-2 py-2 text-right align-bottom text-xs font-medium normal-case leading-tight text-gray-500 dark:text-gray-400 ${
                  muted(col) ? 'italic' : ''
                }`}
              >
                <span className="inline-flex max-w-[7rem] items-start justify-end text-right">
                  <StatusDot kind={colKind} header={col} />
                  <span className="line-clamp-2 break-words">{col.label}</span>
                </span>
              </th>
            ))}
            {rowTotals && (
              <th scope="col" className={`sticky top-0 ${Z.rail} ${headBg} ${thCls} text-right`}>
                Total
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {table.rows.map((row) => (
            <tr
              key={row.key}
              data-rb-row={row.key}
              data-rb-muted={muted(row) ? 'true' : undefined}
              className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              <RowHeader row={row} sticky />
              {row.cells.map((cell, j) => {
                const col = table.columns[j];
                const quiet = muted(row) || (col ? muted(col) : false);
                return (
                  <td key={col ? col.key : j} className="px-1 py-1 text-right" data-rb-cell={`${row.key}|${col ? col.key : j}`}>
                    <span
                      className={`inline-block min-w-[2.25rem] rounded px-1.5 py-0.5 tabular-nums ${
                        cell.empty
                          ? 'text-gray-300 dark:text-gray-600'
                          : bars
                            ? heatClass(cell.pct, quiet) || 'text-gray-700 dark:text-gray-200'
                            : quiet
                              ? 'text-gray-600 dark:text-gray-300'
                              : 'text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      {cellText(cell.value, table.unit)}
                    </span>
                  </td>
                );
              })}
              {rowTotals && (
                <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-rb-row-total={row.key}>
                  {row.total === null ? '' : cellText(row.total, table.unit)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
        {showFooter && (
          <tfoot>
            <tr className="border-t border-gray-200 dark:border-gray-600" data-rb="footer">
              <th
                scope="row"
                className={`sticky left-0 ${Z.rail} bg-white px-3 py-1.5 text-left text-sm font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200`}
              >
                Total
              </th>
              {table.columnTotals.map((t, j) => (
                <td
                  key={table.columns[j] ? table.columns[j].key : j}
                  data-rb-total-cell={j}
                  className="px-1 py-1.5 text-right font-semibold tabular-nums text-gray-700 dark:text-gray-200"
                >
                  <span className="inline-block min-w-[2.25rem] px-1.5">{t === null ? '' : cellText(t, table.unit)}</span>
                </td>
              ))}
              {rowTotals && (
                <td className="px-3 py-1.5 text-right font-bold tabular-nums text-gray-900 dark:text-gray-100">
                  {table.grandTotal === null ? '' : cellText(table.grandTotal, table.unit)}
                </td>
              )}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/** The result as a table: one value column with bars, or a grid. */
export default function ResultTable(props: ResultTableProps) {
  const single = props.table.columns.length === 1 && props.table.columnAxis.kind === 'measure';
  return single ? <ValueTable {...props} /> : <GridTable {...props} />;
}
